import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Dropdown,
  Input,
} from "antd";
import React, { useCallback, useMemo, useState } from "react";
import {
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import XLSX from "xlsx-js-style";
import {
  HEADERS_EXPORT_BEN,
  HEADERS_EXPORT_DETAIL,
  POST_LOAN_DB2_V2,
  POST_REPORT_IMPOUND,
  POST_INVENTORY_STOCK,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";

const TwoLineHeader = ({ top, bottom }) => (
  <div style={{ textAlign: "center", lineHeight: 1.4 }}>
    <div>{top}</div>
    <div style={{ fontSize: 11, fontWeight: 400, color: "#888" }}>{bottom}</div>
  </div>
);
// ── Helpers ใช้ร่วมกับ table / excel / pdf ────────────
const getContno = (r) => r.CONTNO || "";
const getCustomerName = (r) =>
  `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim();
const formatDate = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "");
const formatSoldDate = (d) =>
  d ? dayjs(d).add(543, "year").format("DD/MM/YYYY") : "";

// คำนวณยอดเงินรายแถว
const calcRow = (r) => {
  const total = Number(r.sold_out_price) || 0;
  const beforeVat = total / 1.07;
  const cost = Number(r.creditDebtorOld) || 0;
  return {
    beforeVat,
    vat: total - beforeVat,
    total,
    cost,
    profit: total - cost,
  };
};

// คำนวณยอดรวม
const calcTotals = (data) =>
  data.reduce(
    (acc, r) => {
      const { beforeVat, vat, total, cost, profit } = calcRow(r);
      return {
        beforeVat: acc.beforeVat + beforeVat,
        vat: acc.vat + vat,
        total: acc.total + total,
        cost: acc.cost + cost,
        profit: acc.profit + profit,
      };
    },
    { beforeVat: 0, vat: 0, total: 0, cost: 0, profit: 0 },
  );

const Main = () => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [signOut] = TokenCheck();
  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [date, setDate] = useState(dayjs());
  const [isFlagAgreement, setIsFlagAgreement] = useState(true);
  const [isFlagEnforce, setIsFlagEnforce] = useState(true);
  const [searchContno, setSearchContno] = useState("");
  // filter ข้อมูลในตาราง ตามวันที่ขาย (sold_out_date)
  const [filterDate, setFilterDate] = useState([null, null]);

  // ── Query ─────────────────────────────────────────────
  const onQuery = () => {
    if (!date) {
      message.warning("กรุณาเลือกวันที่ก่อนค้นหา");
      return;
    }
    queryData({
      lastDate: dayjs(date).format("YYYY-MM-DD"),
    });
  };

  // แปลงข้อมูลจาก DB2 ให้อยู่ในรูป map ตาม CONTNO
  const mapDb2ByContno = (db2Data) =>
    db2Data.reduce((acc, { LOAN, MORTGAGE, CUSTOMER, GUARANTORS }) => {
      if (!LOAN?.CONTNO) return acc;
      acc[LOAN.CONTNO] = {
        LOCAT: LOAN.LOCAT,
        TOTPRC: LOAN.TOTPRC,
        NCSHPRC: LOAN.NCSHPRC,
        SMPAY: LOAN.SMPAY,
        PRODUCTLOAN: LOAN.PRODUCTLOAN,
        ISSUNO: LOAN.ISSUNO,
        TYPE: MORTGAGE?.TYPE,
        MODEL: MORTGAGE?.MODEL,
        BAAB: MORTGAGE?.BAAB,
        COLOR: MORTGAGE?.COLOR,
        REGNO: MORTGAGE?.REGNO,
        ENGNO: MORTGAGE?.ENGNO,
        MANUYR: MORTGAGE?.MANUYR,
        DORECV: MORTGAGE?.DORECV,
        OFFIC: CUSTOMER?.OFFIC,
        OCCUP: CUSTOMER?.OCCUP,
        BIRTHDT: CUSTOMER?.BIRTHDT,
        ADDRESS: CUSTOMER?.ADDRESS ?? [],
        GUARANTORS: GUARANTORS ?? [],
      };
      return acc;
    }, {});

  // ดึงข้อมูลเพิ่มเติมจาก DB2 ถ้าล้มเหลวให้คืน {} เพื่อแสดงข้อมูลหลักต่อได้
  const fetchDb2Map = async (reportData) => {
    // { SCHEMA: "'CONTNO1','CONTNO2'" }
    const schemaPayload = reportData.reduce((acc, { CONTNO, SCHEMA }) => {
      if (!CONTNO || !SCHEMA) return acc;
      acc[SCHEMA] = acc[SCHEMA] ? `${acc[SCHEMA]},'${CONTNO}'` : `'${CONTNO}'`;
      return acc;
    }, {});
    if (!Object.keys(schemaPayload).length) return {};

    try {
      const { data } = await axios.post(POST_LOAN_DB2_V2, schemaPayload, {
        headers: HEADERS_EXPORT_BEN,
      });
      const db2Data = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      return mapDb2ByContno(db2Data);
    } catch (e) {
      console.warn("DB2 failed:", e);
      return {};
    }
  };

  const queryData = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(POST_INVENTORY_STOCK, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });

      const reportData = res.data?.data ?? [];
      if (!reportData.length) {
        setArrayPay([]);
        return;
      }

      const db2Map = await fetchDb2Map(reportData);
      setArrayPay(
        reportData
          .map((item) => ({ ...item, ...db2Map[item.CONTNO] }))
          .sort((a, b) => dayjs(a.SDATE).valueOf() - dayjs(b.SDATE).valueOf()),
      );
    } catch (err) {
      const status = err?.response?.status;
      message.error(status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด");
      if (status === 401) signOut();
      setArrayPay([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterData = useCallback(() => {
    if (!arrayPay.length) return [];

    const keyword = searchContno.trim().toLowerCase();

    return arrayPay.filter((item) => {
      // ✅ ตัด closeContno = 1 ออกเสมอ
      if (item.closeContno === 1) return false;

      // ✅ filter เลขสัญญา / เลขตัวถัง
      if (
        keyword &&
        ![item.new_contno, item.CONTNO, item.STRNO].some((c) =>
          String(c ?? "")
            .toLowerCase()
            .includes(keyword),
        )
      )
        return false;

      // ✅ filter วันที่ขาย
      if (filterDate[0] || filterDate[1]) {
        if (!item.sold_out_date) return false;
        const d = dayjs(item.sold_out_date);
        if (filterDate[0] && d.isBefore(dayjs(filterDate[0]).startOf("day")))
          return false;
        if (filterDate[1] && d.isAfter(dayjs(filterDate[1]).endOf("day")))
          return false;
      }

      // ✅ ทั้งคู่ true → เอาทั้งหมด
      if (isFlagAgreement && isFlagEnforce) return true;

      // ✅ Agreement อย่างเดียว → flagLoanNew = 1
      if (isFlagAgreement && !isFlagEnforce) return item.flagLoanNew === 1;

      // ✅ Enforce อย่างเดียว → flagLoanNew = 2
      if (!isFlagAgreement && isFlagEnforce) return item.flagLoanNew === 2;

      // ✅ ทั้งคู่ false → ไม่มีข้อมูล
      return false;
    });
  }, [arrayPay, isFlagAgreement, isFlagEnforce, searchContno, filterDate]);

  // ── Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    // {
    //   title: "สาขา",
    //   align: "center",
    //   width: 60,
    //   render: (_, r) => (
    //     <>
    //       <div style={{ fontWeight: 600 }}>{r.LOCAT || "-"}</div>
    //     </>
    //   ),
    // },
    {
      title: "เลขตัวถัง",
      align: "center",
      width: 60,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.STRNO || "-"}</div>
        </>
      ),
    },
    // {
    //   title: <TwoLineHeader top="เลขที่ใบเสร็จ" bottom="วันที่ออกใบเสร็จ" />,
    //   key: "doc_no",
    //   align: "center",
    //   width: 130,
    //   render: (_, r) => (
    //     <div>
    //       {r.doc_no || "-"}
    //       <br />
    //       {dayjs(r.doc_dt).format("DD/MM/YYYY")}
    //     </div>
    //   ),
    // },
    {
      title: "เลขสัญญา",
      key: "contract_model",
      align: "center",
      width: 130,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{getContno(r) || "-"}</div>
        </>
      ),
    },
    {
      title: <TwoLineHeader top="วันทำสัญญา" bottom="ชื่อลูกค้า" />,
      key: "name_strno",
      align: "left",
      width: 140,
      render: (_, r) => (
        <div>
          {formatDate(r.SDATE) || "-"}
          <br />
          {getCustomerName(r) || "-"}
        </div>
      ),
    },

    {
      title: "วันที่ขาย",
      align: "center",
      width: 90,
      sorter: (a, b) =>
        dayjs(a.sold_out_date).valueOf() - dayjs(b.sold_out_date).valueOf(),
      render: (_, r) => (
<div>{formatSoldDate(r.sold_out_date) || "-"}</div>
      ),
    },
    {
      title: "ราคาขาย (ก่อน VAT)",
      key: "totalJugde",
      align: "right",
      width: 100,
      render: (_, r) => <div>{currencyFormatPoint(calcRow(r).beforeVat)}</div>,
    },
    {
      title: "ราคาขาย (รวม)",
      align: "right",
      width: 100,
      render: (_, r) => <div>{currencyFormatPoint(calcRow(r).total)}</div>,
    },
    {
      title: "ต้นทุนเดิม",
      align: "right",
      width: 100,
      render: (_, r) => <div>{currencyFormatPoint(calcRow(r).cost)}</div>,
    },
    {
      title: "กำไร",
      align: "right",
      width: 100,
      render: (_, r) => <div>{currencyFormatPoint(calcRow(r).profit)}</div>,
    },
  ];

  // ── Export Excel ──────────────────────────────────────
  const handleExportExcel = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const toDate = date ? dayjs(date).format("DD-MM-YYYY") : "";

    const rows = filteredData.map((r, i) => {
      const { beforeVat, vat, total, cost, profit } = calcRow(r);
      return {
        ลำดับ: i + 1,
        เลขตัวถัง: r.STRNO || "",
        เลขสัญญา: getContno(r),
        วันทำสัญญา: formatDate(r.SDATE),
        ชื่อลูกค้า: getCustomerName(r),
        วันที่ขาย: formatSoldDate(r.sold_out_date),
        "ราคาขาย (ก่อน VAT)": beforeVat,
        "vat (7%)": vat,
        "ราคาขาย (รวม)": total,
        ต้นทุนเดิม: cost,
        กำไร: profit,
      };
    });

    const moneyKeys = [
      "ราคาขาย (ก่อน VAT)",
      "vat (7%)",
      "ราคาขาย (รวม)",
      "ต้นทุนเดิม",
      "กำไร",
    ];
    const t = calcTotals(filteredData);

    const summaryRow = {
      ลำดับ: `รวมทั้งหมด ${filteredData.length} รายการ`,
      เลขตัวถัง: "",
      เลขสัญญา: "",
      วันทำสัญญา: "",
      ชื่อลูกค้า: "",
      วันที่ขาย: "",
      "ราคาขาย (ก่อน VAT)": t.beforeVat,
      "vat (7%)": t.vat,
      "ราคาขาย (รวม)": t.total,
      ต้นทุนเดิม: t.cost,
      กำไร: t.profit,
    };

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows);
    const keys = Object.keys(rows[0]);

    const borderStyle = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    const getAlignment = (key) => {
      if (key === "ชื่อลูกค้า") return "left";
      if (moneyKeys.includes(key)) return "right";
      return "center";
    };

    // header
    keys.forEach((_, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "344962" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderStyle,
      };
    });

    // data rows
    rows.forEach((_, ri) => {
      const fill = {
        patternType: "solid",
        fgColor: { rgb: ri % 2 === 0 ? "EBF5FF" : "FFFFFF" },
      };
      keys.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };
        const isMoney = moneyKeys.includes(key);
        ws[cellRef].s = {
          fill,
          font: { sz: 11 },
          alignment: { horizontal: getAlignment(key), vertical: "center" },
          border: borderStyle,
          numFmt: isMoney ? "#,##0.00" : undefined,
        };
      });
    });

    // summary row
    const summaryRowIdx = rows.length + 1;
    keys.forEach((key, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: summaryRowIdx, c: ci });
      if (!ws[cellRef]) return;
      const isMoney = moneyKeys.includes(key);
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "D5E8D4" } },
        font: { bold: true, sz: 12 },
        alignment: { horizontal: getAlignment(key), vertical: "center" },
        border: borderStyle,
        numFmt: isMoney ? "#,##0.00" : undefined,
      };
    });

    // รวมเซลล์ข้อความสรุป (ลำดับ → วันที่ขาย)
    const firstMoneyCol = keys.findIndex((k) => moneyKeys.includes(k));
    ws["!merges"] = [
      {
        s: { r: summaryRowIdx, c: 0 },
        e: { r: summaryRowIdx, c: firstMoneyCol - 1 },
      },
    ];
    const summaryLabelRef = XLSX.utils.encode_cell({ r: summaryRowIdx, c: 0 });
    ws[summaryLabelRef].s = {
      ...ws[summaryLabelRef].s,
      alignment: { horizontal: "center", vertical: "center" },
    };

    ws["!cols"] = keys.map((key) => ({ wch: Math.max(key.length * 2.5, 16) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(wb, `รายงานสินค้าคงเหลือ_${toDate}.xlsx`);
    message.success("Export Excel สำเร็จ");
  };

  // ── Export PDF ────────────────────────────────────────
  const handlePrintPDF = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");
    const toDate = date ? dayjs(date).format("DD-MM-YYYY") : "";

    const drawHeader = (pageNum) => {
      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(16);
      doc.text("รายงานสินค้าคงเหลือ", pageWidth / 2, 8, { align: "center" });
      doc.text(
        `ข้อมูล ณ วันที่  ${dayjs(date).format("DD/MM/YYYY")}`,
        pageWidth / 2,
        14,
        { align: "center" },
      );
      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(14);
      doc.text("บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)", 8, 20);
      doc.text(`วันที่พิมพ์ ${now}`, 8, 26);
      doc.text(`หน้าที่ ${pageNum}`, pageWidth - 8, 26, { align: "right" });
    };

    const tableBody = filteredData.map((r, i) => {
      const { beforeVat, total, cost, profit } = calcRow(r);
      return [
        i + 1,
        r.STRNO || "-",
        getContno(r) || "-",
        `${formatDate(r.SDATE) || "-"}\n${getCustomerName(r) || "-"}`,
        formatSoldDate(r.sold_out_date) || "-",
        currencyFormatPoint(beforeVat),
        currencyFormatPoint(total),
        currencyFormatPoint(cost),
        currencyFormatPoint(profit),
      ];
    });

    const t = calcTotals(filteredData);
    const summaryStyle = {
      fontStyle: "bold",
      font: "THSarabunNew",
      fillColor: [236, 240, 241],
    };

    const summaryRow = [
      {
        content: `รวมทั้งหมด ${filteredData.length} รายการ`,
        colSpan: 5,
        styles: { ...summaryStyle, halign: "center" },
      },
      {
        content: currencyFormatPoint(t.beforeVat),
        styles: { ...summaryStyle, halign: "right" },
      },
      {
        content: currencyFormatPoint(t.total),
        styles: { ...summaryStyle, halign: "right" },
      },
      {
        content: currencyFormatPoint(t.cost),
        styles: { ...summaryStyle, halign: "right" },
      },
      {
        content: currencyFormatPoint(t.profit),
        styles: { ...summaryStyle, halign: "right" },
      },
    ];

    doc.autoTable({
      startY: 30,
      head: [
        [
          "ลำดับ",
          "เลขตัวถัง",
          "เลขสัญญา",
          "วันทำสัญญา\nชื่อลูกค้า",
          "วันที่ขาย",
          "ราคาขาย (ก่อน VAT)",
          "ราคาขาย (รวม)",
          "ต้นทุนเดิม",
          "กำไร",
        ],
      ],
      body: [...tableBody, summaryRow],
      headStyles: {
        font: "THSarabunNew",
        fillColor: [52, 73, 94],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 13,
      },
      columnStyles: {
        3: { halign: "left" }, // วันทำสัญญา/ชื่อลูกค้า
        5: { halign: "right" }, // ราคาขาย (ก่อน VAT)
        6: { halign: "right" }, // ราคาขาย (รวม)
        7: { halign: "right" }, // ต้นทุนเดิม
        8: { halign: "right" }, // กำไร
      },
      tableWidth: "auto",
      margin: { top: 30, left: 8, right: 8 },
      alternateRowStyles: { fillColor: [235, 245, 255] },
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        cellPadding: 1,
        halign: "center",
        fillColor: [255, 255, 255],
      },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
    });

    doc.save(`รายงานสินค้าคงเหลือ_${toDate}.pdf`);
  };

  // ✅ Dropdown menu เลือก PDF หรือ Excel
  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  const filteredData = useMemo(() => handleFilterData(), [handleFilterData]);

  return (
    <>
      <style>{`
      .row-even td { background-color: #f5f8ff !important; }
      .row-odd td  { background-color: #ffffff !important; }
    `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Row>
          <Col span={16}>
            <Space style={{ marginBottom: 10 }}>
              ค้นหาถึง
              <ThaiDatePicker
                size="large"
                value={date}
                onChange={(value) => setDate(value)}
                placeholder={"วันที่สิ้นสุด"}
                allowClear={false}
              />
              <Button size="large" type="primary" onClick={onQuery}>
                ค้นหา
              </Button>
            </Space>
          </Col>
          <Col span={8} style={{ textAlign: "end" }}>
            <Space style={{ marginBottom: 10 }}>
              {/* ✅ Dropdown เลือก PDF / Excel */}
              <Dropdown menu={exportMenuItems} disabled={!filteredData.length}>
                <Button size="large" icon={<PrinterOutlined />}>
                  พิมพ์ / Export <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>

        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>รายงานสินค้าคงเหลือ</Divider>

          <Typography.Text
            strong
            style={{ display: "block", textAlign: "center", fontSize: 16 }}
          >
            บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)
          </Typography.Text>

          {date && (
            <Typography.Text
              style={{ display: "block", textAlign: "center", fontSize: 16 }}
            >
              ข้อมูล ณ วันที่ {dayjs(date).format("DD/MM/YYYY")}
            </Typography.Text>
          )}

          {/* ── Filter ข้อมูลในตาราง ── */}
          <Space wrap style={{ marginTop: 10 }}>
            วันที่ขาย
            <ThaiDatePicker
              value={filterDate[0]}
              onChange={(value) => setFilterDate([value, filterDate[1]])}
              placeholder={"วันที่เริ่มต้น"}
              allowClear
              size="large"
            />
            ถึง
            <ThaiDatePicker
              value={filterDate[1]}
              onChange={(value) => setFilterDate([filterDate[0], value])}
              placeholder={"วันที่สิ้นสุด"}
              allowClear
              size="large"
            />
            <Input
              allowClear
              placeholder="ค้นหาเลขสัญญา / เลขตัวถัง"
              value={searchContno}
              onChange={(e) => setSearchContno(e.target.value)}
              style={{ width: 200 }}
              size="small"
            />
            <Button
              onClick={() => {
                setFilterDate([null, null]);
                setSearchContno("");
              }}
            >
              ล้างตัวกรอง
            </Button>
          </Space>

          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={(r) => r.idJudge ?? r.CONTNO}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            }
            scroll={{ x: 1400 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            summary={() => {
              if (!filteredData.length) return null;
              const t = calcTotals(filteredData);
              const cs = { fontWeight: "bold", background: "#ecf0f1" };
              const cr = {
                fontWeight: "bold",
                background: "#ecf0f1",
                textAlign: "right",
              };

              return (
                <Table.Summary.Row>
                  {/* ลำดับ, เลขตัวถัง, เลขสัญญา, วันทำสัญญา/ชื่อ, วันที่ขาย → 5 ช่อง */}
                  <Table.Summary.Cell
                    index={0}
                    colSpan={5}
                    align="center"
                    style={cs}
                  >
                    รวมทั้งหมด {filteredData.length} รายการ
                  </Table.Summary.Cell>

                  {/* ราคาขาย (ก่อน VAT) */}
                  <Table.Summary.Cell index={5} align="right" style={cr}>
                    {currencyFormatPoint(t.beforeVat)}
                  </Table.Summary.Cell>

                  {/* ราคาขาย (รวม) */}
                  <Table.Summary.Cell index={6} align="right" style={cr}>
                    {currencyFormatPoint(t.total)}
                  </Table.Summary.Cell>

                  {/* ต้นทุนเดิม */}
                  <Table.Summary.Cell index={7} align="right" style={cr}>
                    {currencyFormatPoint(t.cost)}
                  </Table.Summary.Cell>

                  {/* กำไร */}
                  <Table.Summary.Cell index={8} align="right" style={cr}>
                    {currencyFormatPoint(t.profit)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      </Spin>
    </>
  );
};

const MainPendingSellingCar = MotionHoc(Main);
export default MainPendingSellingCar;
